import { useCallback } from 'react';
import { useSearchParams } from 'react-router';

/**
 * Query-string-backed toolbar state for the list pages that are NOT refine
 * tables (approvals, schedules) — refine's own `syncWithLocation` does this
 * job for the rest. Without it those pages hold their filters in plain
 * `useState`, so a reload or a shared link silently drops back to defaults.
 *
 * Values equal to the default are omitted from the URL, which keeps an
 * untouched page's address clean and makes "restore" and "reset" the same
 * operation. Pass a MODULE-LEVEL defaults object: it is a dependency of the
 * setter, so a fresh literal per render would rebuild it every time.
 */
export function urlStateFrom<T extends Record<string, string>>(
  params: URLSearchParams,
  defaults: T,
): T {
  const state = { ...defaults };
  for (const key of Object.keys(defaults)) {
    const value = params.get(key);
    if (value !== null) state[key as keyof T] = value as T[keyof T];
  }
  return state;
}

export function urlStateToParams<T extends Record<string, string>>(
  state: T,
  defaults: T,
): URLSearchParams {
  const params = new URLSearchParams();
  for (const key of Object.keys(defaults)) {
    if (state[key] !== defaults[key]) params.set(key, state[key]);
  }
  return params;
}

export function useUrlState<T extends Record<string, string>>(
  defaults: T,
): [T, (patch: Partial<T>) => void] {
  const [params, setParams] = useSearchParams();

  const patch = useCallback(
    (next: Partial<T>) => {
      // `replace` — typing in a filter box should not stack history entries.
      setParams(
        (current) => urlStateToParams({ ...urlStateFrom(current, defaults), ...next }, defaults),
        { replace: true },
      );
    },
    [setParams, defaults],
  );

  return [urlStateFrom(params, defaults), patch];
}
